import { Typography } from '@neo/primitives/typography'
import { buildScreeningWireIdMap } from '../../config/outbound-screening.config'
import { useFlowStore } from '../../store'
import { humanizeSkipReason, type ParsedVariable, parseVariables } from '../analysis/utils'
import { useFlowRunStore } from '../store'
import type { NodeExecutedEvent, SystemChunkCreatedEvent } from '../types'

// Per-node task progress (SER-5959): every task the node owns as a checklist —
// pending / active (live dot) / done (+ recorded value) / skipped (+ reason).
// Rows come from the node's builder config when resolvable; TASK_STARTED and
// VARIABLE_SKIPPED chunks (and value chunks via aggregated_variables) drive the
// states, so unresolvable configs still materialize rows from events alone.
// While the node is the run's last executed node the panel is live; afterwards
// it stays as the node's receipt.

const CONFIRM_ID = '__confirm_known_values__'

type RowState = 'pending' | 'active' | 'done' | 'skipped'

interface TaskRow {
  id: string
  label: string
  state: RowState
  value?: string
  reason?: string
}

interface AgentTasksLikeConfig {
  agent_tasks?: {
    variables?: Array<{ name: string; known_value?: string; confirm_if_known?: boolean }>
    extract_only?: Array<{ name: string }>
  }
  screening_task?: { ask_permission?: boolean }
  ask_permission?: boolean
}

const resolveNodeConfig = (nodeUuid: string): AgentTasksLikeConfig | null => {
  const instance = useFlowStore.getState().flowInstance
  if (!instance) return null
  const direct = instance.getNode(nodeUuid)
  if (direct) return (direct.data?.config ?? null) as AgentTasksLikeConfig | null
  // The outbound-screening composite's screening half carries a minted wire id;
  // remap it back onto the composite canvas node.
  const canvasId = buildScreeningWireIdMap(instance.getNodes()).get(nodeUuid)
  if (!canvasId) return null
  return (instance.getNode(canvasId)?.data?.config ?? null) as AgentTasksLikeConfig | null
}

const rowsFromConfig = (
  config: AgentTasksLikeConfig | null,
): { rows: TaskRow[]; captured: string[] } => {
  if (!config) return { rows: [], captured: [] }
  const agentTasks = config.agent_tasks
  if (agentTasks?.variables?.length || agentTasks?.extract_only?.length) {
    const rows: TaskRow[] = []
    const variables = agentTasks.variables ?? []
    if (variables.some((v) => v.known_value !== undefined && v.confirm_if_known)) {
      rows.push({ id: CONFIRM_ID, label: 'Confirm known values', state: 'pending' })
    }
    for (const v of variables) rows.push({ id: v.name, label: v.name, state: 'pending' })
    return { rows, captured: (agentTasks.extract_only ?? []).map((v) => v.name) }
  }
  const askPermission = config.screening_task?.ask_permission ?? config.ask_permission
  if (askPermission !== undefined) {
    const rows: TaskRow[] = [{ id: 'identify', label: 'Identify contact', state: 'pending' }]
    if (askPermission) {
      rows.push({ id: 'permission', label: 'Permission to continue', state: 'pending' })
    }
    return { rows, captured: [] }
  }
  return { rows: [], captured: [] }
}

const foldEvents = (
  rows: TaskRow[],
  node: NodeExecutedEvent,
  parsedVariables: ParsedVariable[],
  live: boolean,
): TaskRow[] => {
  const byId = new Map(rows.map((row) => [row.id, row]))
  const ordered = [...rows]
  const ensure = (id: string, label?: string): TaskRow => {
    let row = byId.get(id)
    if (!row) {
      row = { id, label: label ?? id, state: 'pending' }
      byId.set(id, row)
      ordered.push(row)
    } else if (label && row.id === CONFIRM_ID) {
      row.label = label
    }
    return row
  }

  for (const chunk of node.chunks ?? []) {
    if (chunk.event_name !== 'SYSTEM_CHUNK_CREATED') continue
    const sys = chunk as SystemChunkCreatedEvent
    if (sys.type === 'TASK_STARTED' && sys.metadata?.task) {
      for (const row of ordered) {
        if (row.state === 'active') row.state = 'done'
      }
      ensure(sys.metadata.task, sys.metadata.label ?? undefined).state = 'active'
    }
    if (sys.type === 'VARIABLE_SKIPPED' && sys.metadata?.variable) {
      const row = ensure(sys.metadata.variable)
      row.state = 'skipped'
      row.reason = humanizeSkipReason(sys.metadata.reason)
    }
  }

  for (const variable of parsedVariables) {
    const row = byId.get(variable.variable_name)
    if (row && row.state !== 'skipped') {
      row.state = row.state === 'active' && live ? 'active' : 'done'
      row.value = variable.variable_value
    }
  }

  if (!live) {
    for (const row of ordered) {
      if (row.state === 'active') row.state = 'done'
    }
  }
  return ordered
}

const StateDot = ({ state }: { state: RowState }) => {
  if (state === 'done')
    return <span className="text-status-success text-xs-600 w-3.5 text-center">✓</span>
  if (state === 'skipped')
    return <span className="text-tertiary text-xs-600 w-3.5 text-center">−</span>
  return (
    <span className="w-3.5 flex justify-center">
      <span
        className={`rounded-full ${
          state === 'active' ? 'h-1.5 w-1.5 bg-status-progress animate-pulse' : 'h-1 w-1 bg-st1'
        }`}
      />
    </span>
  )
}

export const TaskProgress = ({ node }: { node: NodeExecutedEvent }) => {
  const live = useFlowRunStore(
    (state) =>
      state.isFlowRunning && state.executionHistory.at(-1)?.node_run_uuid === node.node_run_uuid,
  )
  const { rows: configRows, captured } = rowsFromConfig(resolveNodeConfig(node.current_node_uuid))
  const parsedVariables = node.aggregated_variables ? parseVariables(node.aggregated_variables) : []
  const rows = foldEvents(configRows, node, parsedVariables, live)
  if (!rows.length && !captured.length) return null

  const settled = rows.filter((row) => row.state === 'done' || row.state === 'skipped').length
  const capturedValues = parsedVariables.filter((v) => captured.includes(v.variable_name))

  return (
    <div className="bg-card rounded-md px-3 py-2 mb-1 border border-border font-inter">
      <div className="flex items-center px-1 pb-0.5">
        <Typography variant="H4/500" className="text-foreground">
          Skill progress
        </Typography>
        <Typography variant="XS/500" className="ml-auto font-jetbrains text-secondary tabular-nums">
          {settled}/{rows.length}
        </Typography>
      </div>
      {rows.map((row) => (
        <div
          key={row.id}
          className={`flex items-center gap-2 px-1 py-0.5 rounded ${
            row.state === 'active' ? 'bg-bg2' : ''
          }`}
        >
          <StateDot state={row.state} />
          <Typography
            variant={row.state === 'active' ? 'XS/500' : 'XS/400'}
            className={`font-inter truncate ${
              row.state === 'pending' || row.state === 'skipped' ? 'text-tertiary' : ''
            } ${row.state === 'skipped' ? 'line-through' : ''}`}
          >
            {row.label}
          </Typography>
          {(row.value || row.reason) && (
            <Typography
              variant="XS/400"
              className={`ml-auto text-secondary truncate max-w-[45%] ${
                row.reason ? 'font-inter italic' : 'font-jetbrains tabular-nums'
              }`}
            >
              {row.value ?? row.reason}
            </Typography>
          )}
        </div>
      ))}
      {capturedValues.length > 0 && (
        <div className="border-t border-dashed border-st2 mt-1 pt-1 px-1">
          <Typography variant="XS/400" className="text-tertiary">
            Captured at completion:{' '}
            {capturedValues.map((v) => `${v.variable_name} · ${v.variable_value}`).join(', ')}
          </Typography>
        </div>
      )}
    </div>
  )
}
